// Customer auth gate — full-screen sign-in / sign-up surface that renders
// before the immersive runtime mounts. Two visual modes:
//   - normal: Galaxy Galleria neon logo + tagline + form
//   - just-signed-out: Thank-you neon sign instead of logo, ~3s splash,
//     then transitions to the normal mode automatically (click to skip).
//
// Owns its own DOM, listeners, and state. Mounted into the same root the
// runtime would use, swapped out by main.ts once auth flips signed-in.

import {
	signIn as fbSignIn,
	signUp as fbSignUp,
	resetPassword,
	authErrorCopy,
	signInWithGoogle,
	getIdTokenClaims,
	signOut as fbSignOut,
	signInAsGuest,
} from "./firebase";
import { guestEntryAllowed, guestLinkToken, stashGuestToken } from "./guest-entry";
import { isAllowlist } from "./access-mode";
import { googleSignInEnabled, planeWord } from "@runtime/_features.js";
import { sessionHint } from "@runtime/_early-fetch.js";
import { passwordToggleHtml, togglePasswordVisibility } from "@runtime/_password-toggle.js";   // ASK-139 §82 F-205b — the eye on the password field   // ASK-145: a plane without a Google provider draws no Google button

type Mode = "signin" | "signup";

const JUST_SIGNED_OUT_KEY = "kapowx_just_signed_out";
const SIGNED_OUT_SPLASH_MS = 3000;
const PENDING_POLL_MS = 30_000;
const BASE = import.meta.env.BASE_URL;

export class AuthGate {
	private root: HTMLElement;
	private mode: Mode = "signin";
	private submitting = false;
	private generalError: string | null = null;
	private fieldErrors: { email?: string; password?: string } = {};
	private resetSent = false;
	private passwordShown = false;   // F-205b: survives the form's re-render, so the field comes back as she left it
	private splashActive: boolean;
	private pendingEmail: string | null = null;
	private pendingPollTimer: ReturnType<typeof setInterval> | null = null;
	private onApproved: (() => void) | null = null;
	// ASK-159 §2.17 item 8 (card 123qy9rtt0a) — a cached session whose token cannot be refreshed because the sign-in
	// hosts do not answer (`auth/network-request-failed`: DNS / offline / a filter). Never a bare console error: words,
	// a Retry, and a way out that needs no network.
	private network: NetworkFailure | null = null;
	private lastErrorCode: string | null = null;
	// ASK-159 §2.18b D1 — what a FORM sign-in submitted, written back into the fields on every re-render (innerHTML
	// would empty them). A NETWORK failure keeps email AND password, so the inline Retry re-submits them; any other
	// refusal keeps the email and clears the password (Firebase UI / Auth0 Universal Login / Google sign-in).
	private draft: { email: string; password: string } | null = null;
	// ASK-172 Client 5b item 1 — "Continue as a guest": offered only on a meet link where the plane's public door says
	// presence.anonymousMayFollow (dev). Never drawn on prod (the door says false), never drawn without a link.
	private guestOffer = false;

	constructor(root: HTMLElement, opts?: { pendingEmail?: string | null; onApproved?: () => void; network?: NetworkFailure }) {
		this.root         = root;
		this.pendingEmail = opts?.pendingEmail ?? null;
		this.onApproved   = opts?.onApproved ?? null;
		this.network      = opts?.network ?? null;
		this.splashActive = !this.pendingEmail && !this.network && sessionStorage.getItem(JUST_SIGNED_OUT_KEY) === "1";
		if (this.splashActive) {
			sessionStorage.removeItem(JUST_SIGNED_OUT_KEY);
			// Auto-dismiss the splash after a few seconds. Click anywhere also
			// dismisses early — see _bindEvents.
			setTimeout(() => this._dismissSplash(), SIGNED_OUT_SPLASH_MS);
		}
		this._render();
		this._bindEvents();
		if (this.pendingEmail) this._startPendingPoll();
		if (!this.pendingEmail && !this.network) {
			void guestEntryAllowed().then((ok) => {
				if (!ok || this.guestOffer) return;
				this.guestOffer = true;
				// keep whatever was typed while the door answered (a re-render empties the fields)
				const f = (n: string) => this.root.querySelector<HTMLInputElement>(`[data-auth-form] input[name="${n}"]`)?.value ?? "";
				const typed = { email: f("email"), password: f("password") };
				this._render();
				if (!this.draft && (typed.email || typed.password)) {
					const e = this.root.querySelector<HTMLInputElement>('[data-auth-form] input[name="email"]'), p = this.root.querySelector<HTMLInputElement>('[data-auth-form] input[name="password"]');
					if (e) e.value = typed.email; if (p) p.value = typed.password;
				}
			});
		}
	}

	// Public hook so main.ts can mark the splash flag before triggering
	// sign-out; the next auth-state-change recreates the gate, sees the
	// flag, and shows the thank-you splash.
	static markSignedOut(): void {
		try { sessionStorage.setItem(JUST_SIGNED_OUT_KEY, "1"); } catch {}
		sessionHint(false);   // D-088 Slice 0: a signed-out profile never fetches the street ahead of auth
	}

	destroy(): void {
		this._stopPendingPoll();
		// Nothing externally held; clearing innerHTML drops all listeners.
		this.root.innerHTML = "";
	}

	// ── Rendering ─────────────────────────────────────────────────────────

	private _render(): void {
		let body: string;
		if (this.network) body = this._renderNetwork();
		else if (this.pendingEmail) body = this._renderPending();
		else if (this.splashActive) body = this._renderSplash();
		else body = this._renderForm();
		this.root.innerHTML = `
			<div class="kapowx-auth-gate" data-auth-gate>
				${body}
			</div>
		`;
		// Written as DOM properties, never into the markup, so the password is never an HTML attribute.
		if (this.draft && !this.network && !this.pendingEmail && !this.splashActive) {
			const email = this.root.querySelector<HTMLInputElement>('[data-auth-form] input[name="email"]');
			const password = this.root.querySelector<HTMLInputElement>('[data-auth-form] input[name="password"]');
			if (email) email.value = this.draft.email;
			if (password) password.value = this.draft.password;
		}
	}

	private _renderPending(): string {
		return `<div class="kapowx-auth-gate__container kapowx-auth-gate__container--pending"><img class="kapowx-auth-gate__logo" src="${BASE}branding/galaxy-galleria-logo.png" alt="Galaxy Galleria"><div class="kapowx-auth-gate__pending-title">Almost there</div><div class="kapowx-auth-gate__pending-body">Your account <strong>${escapeHtml(this.pendingEmail!)}</strong> is waiting for admin approval. You'll be able to enter as soon as it's reviewed.</div><div class="kapowx-auth-gate__pending-spinner" aria-hidden="true"></div><button class="kapowx-auth-gate__link" type="button" data-auth-pending-signout>Sign out</button></div>`;
	}

	private _renderNetwork(): string {
		const n = this.network!, C = "kapowx-auth-gate__", off = n.busy ? " disabled" : "";
		return `<div class="${C}container ${C}container--pending" data-auth-network="${escapeHtml(n.code)}"><img class="${C}logo" src="${BASE}branding/galaxy-galleria-logo.png" alt="Galaxy Galleria">`
			+ `<div class="${C}pending-title">Cannot reach the sign-in service</div><div class="${C}pending-body" role="alert" data-auth-network-message>${escapeHtml(networkCopy(n.code))}`
			+ `${n.email ? `<br>Signed in as <strong>${escapeHtml(n.email)}</strong>.` : ""}${n.tries ? `<br>Still no answer (tried ${n.tries}×).` : ""}</div>`
			+ `<button class="${C}submit" type="button" data-auth-retry${off}>${n.busy ? "Trying…" : "Retry"}</button><button class="${C}link" type="button" data-auth-network-signout${off}>Sign out / use another account</button></div>`;
	}

	private async _retryNetwork(): Promise<void> {
		const n = this.network;
		if (!n || n.busy) return;
		n.busy = true; this._render();
		try { await n.onRetry(); }   // success swaps the surface (main.ts); a throw = still unreachable
		catch (err) { n.code = (err as { code?: string })?.code || n.code; n.tries++; }
		finally { n.busy = false; if (this.root.querySelector("[data-auth-network]")) this._render(); }
	}

	private _renderSplash(): string {
		return `<div class="kapowx-auth-gate__splash" data-auth-splash><img class="kapowx-auth-gate__thankyou" src="${BASE}branding/thank-you.png" alt="Thank you for visiting Galaxy Galleria"><div class="kapowx-auth-gate__splash-hint">Tap anywhere to continue</div></div>`;
	}

	private _renderForm(): string {
		const isSignUp   = this.mode === "signup";
		const title      = isSignUp ? "Create your account" : "Sign in to shop the Galaxy";
		const submitText = isSignUp ? "Create account" : "Sign in";
		const toggleText = isSignUp ? "Already have an account?" : "New here?";
		const toggleCta  = isSignUp ? "Sign in" : "Create an account";

		// When the user hits "email already in use" while trying to sign up,
		// surface a one-click switch-to-sign-in button inline with the error.
		// Plain text "Sign in instead?" was confusing — readers had to find
		// the separate toggle below the form.
		const offerSignInSwitch = isSignUp && this.generalError?.includes("already exists");
		const offerRetry = this.lastErrorCode === "auth/network-request-failed";
		const generalBanner = this.generalError
			? `<div class="kapowx-auth-gate__banner kapowx-auth-gate__banner--error">${escapeHtml(this.generalError)}${
				offerSignInSwitch
					? ` <button class="kapowx-auth-gate__link" type="button" data-auth-toggle>Switch to sign in</button>`
					: ""
			}${offerRetry ? ` <button class="kapowx-auth-gate__link" type="button" data-auth-retry-submit>Retry</button>` : ""}</div>`
			: "";
		const resetBanner = this.resetSent
			? `<div class="kapowx-auth-gate__banner kapowx-auth-gate__banner--info">Check your email for a reset link.</div>`
			: "";

		return `<div class="kapowx-auth-gate__container"> <img class="kapowx-auth-gate__logo" src="${BASE}branding/galaxy-galleria-logo.png" alt="Galaxy Galleria"> <div class="kapowx-auth-gate__tagline">${escapeHtml(title)}</div>
 ${generalBanner} ${resetBanner}
 ${this.guestOffer && !isSignUp ? `<div class="kapowx-auth-gate__guest" data-auth-guest-block><button class="kapowx-auth-gate__google kapowx-auth-gate__guest-btn" type="button" data-auth-guest ${this.submitting ? "disabled" : ""}>Continue as a guest</button><p class="kapowx-auth-gate__guest-note">Your friend's link: look around with them and read the party's chat. Sign in to talk.</p></div> <div class="kapowx-auth-gate__divider"><span>or sign in</span></div>` : ""}
 ${googleSignInEnabled() ? `<button class="kapowx-auth-gate__google" type="button" data-auth-google ${this.submitting ? "disabled" : ""}> <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.4 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.5-5.8c-2 1.4-4.7 2.3-7.7 2.3-6.3 0-11.7-3.9-13.6-9.3l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg> Continue with Google </button> <div class="kapowx-auth-gate__divider"><span>or</span></div>` : ""}
 <form class="kapowx-auth-gate__form" data-auth-form novalidate> <label class="kapowx-auth-gate__field${this.fieldErrors.email ? " has-error" : ""}"> <span class="kapowx-auth-gate__label">Email</span> <input class="kapowx-auth-gate__input" type="email" name="email" autocomplete="email" required ${this.submitting ? "disabled" : ""} /> ${this.fieldErrors.email ? `<span class="kapowx-auth-gate__error">${escapeHtml(this.fieldErrors.email)}</span>` : ""} </label>
 <label class="kapowx-auth-gate__field${this.fieldErrors.password ? " has-error" : ""}"> <span class="kapowx-auth-gate__label">Password</span> <span class="kapowx-auth-gate__pw"> <input class="kapowx-auth-gate__input" type="${this.passwordShown ? "text" : "password"}" name="password" autocomplete="${isSignUp ? "new-password" : "current-password"}" required minlength="6" ${this.submitting ? "disabled" : ""} /> ${passwordToggleHtml(this.passwordShown, this.submitting)} </span> ${this.fieldErrors.password ? `<span class="kapowx-auth-gate__error">${escapeHtml(this.fieldErrors.password)}</span>` : ""} </label>
 <button class="kapowx-auth-gate__submit" type="submit" ${this.submitting ? "disabled" : ""}> ${this.submitting ? "Working…" : escapeHtml(submitText)} </button>
 ${isSignUp ? "" : ` <button class="kapowx-auth-gate__link" type="button" data-auth-reset ${this.submitting ? "disabled" : ""}> Forgot password? </button> `} </form>
 <div class="kapowx-auth-gate__toggle"> ${escapeHtml(toggleText)} <button class="kapowx-auth-gate__link" type="button" data-auth-toggle ${this.submitting ? "disabled" : ""}> ${escapeHtml(toggleCta)} </button> </div> ${isAllowlist() ? `<p class="kapowx-auth-gate__alpha" data-auth-alpha>Invited guests only during the ${planeWord()}.</p>` : ""} </div> `;
	}

	private async _handleGoogle(): Promise<void> {
		if (this.submitting) return;
		this.submitting = true;
		this.generalError = null;
		this.draft = null;
		this._render();
		try {
			await signInWithGoogle();
			// subscribeAuth in main.ts handles the transition; nothing to do.
		} catch (err) {
			const code = (err as { code?: string })?.code || "unknown";
			this.lastErrorCode = code;
			this.generalError = authErrorCopy(code);
		} finally {
			this.submitting = false;
			this._render();
		}
	}

	private async _handleGuest(): Promise<void> {
		if (this.submitting) return;
		this.submitting = true;
		this.generalError = null;
		this._render();
		try {
			if (!(await guestEntryAllowed())) throw Object.assign(new Error("guest entry closed"), { code: "auth/operation-not-allowed" });
			stashGuestToken(await guestLinkToken());   // item 0: the link survives the reload into the runtime
			await signInAsGuest();
			// subscribeAuth in main.ts sees the anonymous user and reloads into the runtime (the meet arrival).
		} catch (err) {
			const code = (err as { code?: string })?.code || "unknown";
			this.lastErrorCode = code;
			this.generalError = authErrorCopy(code);
			this.submitting = false;
			this._render();
		}
	}

	// ── Events ────────────────────────────────────────────────────────────

	private _bindEvents(): void {
		this.root.addEventListener("click", (e) => {
			const target = e.target as HTMLElement;
			if (this.splashActive && target.closest("[data-auth-splash]")) {
				this._dismissSplash();
				return;
			}
			const pwToggle = target.closest<HTMLButtonElement>("[data-auth-pw-toggle]");
			if (pwToggle) {
				// In place — a re-render would drop what she typed (F-205b).
				const input = this.root.querySelector<HTMLInputElement>('input[name="password"]');
				this.passwordShown = togglePasswordVisibility(pwToggle, input);
				return;
			}
			if (target.closest("[data-auth-retry]")) { void this._retryNetwork(); return; }
			if (target.closest("[data-auth-network-signout]")) {
				// Clears the LOCAL session only — needs no network (the sign-in hosts are what failed). main.ts hears the
				// signed-out transition and draws the sign-in form (no thank-you splash: she is changing account).
				void signOutLocal();
				return;
			}
			if (target.closest("[data-auth-retry-submit]")) {
				const form = this.root.querySelector<HTMLFormElement>("[data-auth-form]");
				if (form) void this._submitAuth(form);
				return;
			}
			if (target.closest("[data-auth-guest]")) {
				void this._handleGuest();
				return;
			}
			if (target.closest("[data-auth-google]")) {
				void this._handleGoogle();
				return;
			}
			if (target.closest("[data-auth-pending-signout]")) {
				// Sign out from the pending screen — clears the Firebase session
				// + marks the just-signed-out splash flag, so the next gate
				// shows the thank-you neon before the form.
				AuthGate.markSignedOut();
				void fbSignOut();
				return;
			}
			if (target.closest("[data-auth-toggle]")) {
				this.mode = this.mode === "signin" ? "signup" : "signin";
				this.draft = null;
				this.fieldErrors = {};
				this.generalError = null;
				this.resetSent = false;
				this._render();
				return;
			}
			if (target.closest("[data-auth-reset]")) {
				void this._submitReset();
				return;
			}
		});
		this.root.addEventListener("submit", (e) => {
			if ((e.target as HTMLElement).matches("[data-auth-form]")) {
				e.preventDefault();
				void this._submitAuth(e.target as HTMLFormElement);
			}
		});
	}

	// Polls the user's ID token every 30s while in pending-approval state.
	// When the admin clicks Approve in their email, the Cloud Function sets
	// `approved: true` as a custom claim on the user. Firebase's local
	// cache won't pick that up until the next ~1h refresh, so we
	// force-refresh on each poll and check the freshly-issued claims.
	// Once approved, fires the onApproved callback (main.ts mounts the
	// runtime) without reloading the page.
	private _startPendingPoll(): void {
		if (this.pendingPollTimer) return;
		this.pendingPollTimer = setInterval(async () => {
			try {
				const claims = await getIdTokenClaims(true);
				if (claims?.approved === true) {
					this._stopPendingPoll();
					this.onApproved?.();
				}
			} catch (err) {
				// Force-refresh can fail on transient network blips. Silently
				// retry on the next tick — no need to surface unless persistent.
				console.warn("[auth-gate] pending poll error:", err);
			}
		}, PENDING_POLL_MS);
	}

	private _stopPendingPoll(): void {
		if (this.pendingPollTimer) {
			clearInterval(this.pendingPollTimer);
			this.pendingPollTimer = null;
		}
	}

	private _dismissSplash(): void {
		if (!this.splashActive) return;
		this.splashActive = false;
		this._render();
	}

	// ── Submit handlers ───────────────────────────────────────────────────

	private async _submitAuth(form: HTMLFormElement): Promise<void> {
		const email    = (form.elements.namedItem("email")    as HTMLInputElement)?.value.trim() ?? "";
		const password = (form.elements.namedItem("password") as HTMLInputElement)?.value         ?? "";

		const errors: { email?: string; password?: string } = {};
		if (!email)                      errors.email    = "Required";
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Doesn't look right";
		if (!password)                   errors.password = "Required";
		else if (password.length < 6)    errors.password = "At least 6 characters";

		if (Object.keys(errors).length) {
			this.fieldErrors = errors;
			this.generalError = null;
			this.draft = null;
			this._render();
			return;
		}

		this.draft = { email, password };
		this.submitting = true;
		this.fieldErrors = {};
		this.generalError = null;
		this.resetSent = false;
		this._render();

		try {
			if (this.mode === "signup") {
				await fbSignUp(email, password);
			} else {
				await fbSignIn(email, password);
			}
			// Success — onAuthStateChanged in main.ts will swap the gate out.
			// Leave submitting=true so the form stays disabled until the swap.
		} catch (err) {
			const code = (err as { code?: string })?.code || "unknown";
			this.submitting = false;
			this.lastErrorCode = code;
			this.generalError = authErrorCopy(code);
			if (code !== "auth/network-request-failed") this.draft = { email, password: "" };
			this._render();
		}
	}

	private async _submitReset(): Promise<void> {
		// Read the email field directly — reset reuses whatever is typed there.
		const emailInput = this.root.querySelector<HTMLInputElement>('input[name="email"]');
		const email = emailInput?.value.trim() ?? "";
		this.draft = null;
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			this.fieldErrors = { email: "Enter your email above first" };
			this.generalError = null;
			this.resetSent = false;
			this._render();
			return;
		}
		this.submitting = true;
		this._render();
		try {
			await resetPassword(email);
			this.submitting = false;
			this.resetSent = true;
			this.generalError = null;
			this.fieldErrors = {};
			this._render();
		} catch (err) {
			const code = (err as { code?: string })?.code || "unknown";
			this.submitting = false;
			this.generalError = authErrorCopy(code);
			this._render();
		}
	}
}

export type NetworkFailure = { code: string; email: string | null; onRetry: () => Promise<void>; tries: number; busy?: boolean };

/** The words under the "Cannot reach the sign-in service" title for a session that cannot be confirmed: the title
 *  states the condition, the body says what to do — it never repeats the title (ASK-159 §2.17b item 8b). Pure. */
export function networkCopy(code: string): string {
	return code === "auth/network-request-failed"
		? "Check your connection, then press Retry."
		: `Your sign-in could not be confirmed (${code || "unknown"}). Retry, or sign out and sign in again.`;
}

/** Sign out WITHOUT the network: the SDK's own sign-out (local persistence only), bounded; if the session is still
 *  there after it, the SDK's local stores are cleared and the page reloads signed out. */
async function signOutLocal(): Promise<void> {
	try { await Promise.race([fbSignOut(), new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 3000))]); return; }
	catch { /* fall through: clear the stores by hand */ }
	try { for (const k of Object.keys(localStorage)) if (k.startsWith("firebase:")) localStorage.removeItem(k); } catch {}
	try { indexedDB.deleteDatabase("firebaseLocalStorageDb"); } catch {}
	window.location.reload();
}

function escapeHtml(value: string): string {
	return String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}
